<script setup lang="ts">
/**
 * InfoViewLayout – left-side flank column. Hosts contextual information for
 * whichever record is selected in the middle column. `ClientsTab` populates
 * `selectedClientId` in `useClientCardStore`; `PaymentsTab` populates
 * `selectedPaymentId` in `usePaymentCardStore`; `ServicesTab` populates
 * `selectedBusinessServiceId` in `useBusinessServiceCardStore`;
 * `ServiceTypesPane` populates `selectedBusinessServiceTypeId` in
 * `useBusinessServiceTypeCardStore`; `ServiceTypesPane` populates
 * `selectedBusinessServiceNameId` in `useBusinessServiceNameCardStore`.
 * This layout reacts by mounting the corresponding lazily loaded card with
 * that id.
 *
 * When no row is selected the column is intentionally empty: it still
 * reserves its share of horizontal space (`flex: 1 1 0` against
 * `ConversationLayout`) so the middle column does not shift when the user
 * opens or closes the card. Screen readers can skip past the empty state
 * via the `aria-label`.
 */
import { computed, defineAsyncComponent } from 'vue'
import { useClientCardStore } from '@/stores/client_card_store'
import { usePaymentCardStore } from '@/stores/payment_card_store'
import { useBusinessServiceCardStore } from '@/stores/business_service_card_store'
import { useBusinessServiceTypeCardStore } from '@/stores/business_service_type_card_store'
import { useBusinessServiceNameCardStore } from '@/stores/business_service_name_card_store'
import { useAddClientFormStore } from '@/stores/add_client_form_store'
import { useAddPaymentFormStore } from '@/stores/add_payment_form_store'
import { useAddServiceCategoryFormStore } from '@/stores/add_service_category_form_store'
import { useAddServiceTypeFormStore } from '@/stores/add_service_type_form_store'
import { useAddSubscriptionTypeFormStore } from '@/stores/add_subscription_type_form_store'
import { useSubscriptionTypeCardStore } from '@/stores/subscription_type_card_store'
import { useAddBusinessServiceFormStore } from '@/stores/add_business_service_form_store'
import { useBookingTypeCardStore } from '@/stores/booking_type_card_store'
import { useAddBookingTypeFormStore } from '@/stores/add_booking_type_form_store'
import { useGroupCardStore } from '@/stores/group_card_store'
import { useAddGroupFormStore } from '@/stores/add_group_form_store'

/*
 * `ClientCard` is loaded lazily to break a module-evaluation ordering issue
 * in development. The card transitively imports `@/api/http`, which imports
 * `@/router`. That same router has its own circular path through
 * `WelcomeTab` → `@/api/keycloak_api` → `@/config/keycloak_config` →
 * `@/router`. `keycloak_config` calls `router.getRoutes()` at module-eval
 * time, so it requires the `router` binding to be finalized before its
 * module body runs. Statically importing `ClientCard` from this layout
 * makes `App.vue` pull `@/router` *before* its existing
 * `useKeycloakHealth` import, which leaves `router` undefined when
 * `keycloak_config` evaluates and crashes the app at boot.
 *
 * Loading `ClientCard` via `defineAsyncComponent` defers the heavy import
 * chain until the user actually selects a client row — by then both
 * `@/router` and `keycloak_config` have completed their module
 * evaluation, and the cycle is harmless.
 */
const ClientCard = defineAsyncComponent(() => import('@/components/main/clientCard/ClientCard.vue'))
const PaymentCard = defineAsyncComponent(
  () => import('@/components/main/paymentCard/PaymentCard.vue'),
)
const BusinessServiceCard = defineAsyncComponent(
  () => import('@/components/main/businessServiceCard/BusinessServiceCard.vue'),
)
const BusinessServiceTypeCard = defineAsyncComponent(
  () => import('@/components/main/businessServiceTypeCard/BusinessServiceTypeCard.vue'),
)
const ServiceCategoryCard = defineAsyncComponent(
  () => import('@/components/main/serviceCategoryCard/ServiceCategoryCard.vue'),
)
const SubscriptionTypeCard = defineAsyncComponent(
  () => import('@/components/main/subscriptionTypeCard/SubscriptionTypeCard.vue'),
)
/*
 * Same deferred-import rationale as `ClientCard` above: `AddClientForm` and
 * `AddPaymentForm` transitively import `@/api/http` → `@/router`, so they
 * must be loaded lazily to avoid the boot-time circular-evaluation issue.
 */
const AddClientForm = defineAsyncComponent(
  () => import('@/components/main/clientForm/AddClientForm.vue'),
)
const AddPaymentForm = defineAsyncComponent(
  () => import('@/components/main/paymentForm/AddPaymentForm.vue'),
)
const AddServiceCategoryForm = defineAsyncComponent(
  () => import('@/components/main/serviceCategoryForm/AddServiceCategoryForm.vue'),
)
const AddServiceTypeForm = defineAsyncComponent(
  () => import('@/components/main/serviceTypeForm/AddServiceTypeForm.vue'),
)
const AddSubscriptionTypeForm = defineAsyncComponent(
  () => import('@/components/main/subscriptionTypeForm/AddSubscriptionTypeForm.vue'),
)
const AddServiceForm = defineAsyncComponent(
  () => import('@/components/main/serviceForm/AddServiceForm.vue'),
)
const BookingTypeCard = defineAsyncComponent(
  () => import('@/components/main/bookingTypeCard/BookingTypeCard.vue'),
)
const AddBookingTypeForm = defineAsyncComponent(
  () => import('@/components/main/bookingTypeForm/AddBookingTypeForm.vue'),
)
const GroupCard = defineAsyncComponent(() => import('@/components/main/groupCard/GroupCard.vue'))
const AddGroupForm = defineAsyncComponent(
  () => import('@/components/main/groupForm/AddGroupForm.vue'),
)

const clientCardStore = useClientCardStore()
const paymentCardStore = usePaymentCardStore()
const businessServiceCardStore = useBusinessServiceCardStore()
const businessServiceTypeCardStore = useBusinessServiceTypeCardStore()
const businessServiceNameCardStore = useBusinessServiceNameCardStore()
const bookingTypeCardStore = useBookingTypeCardStore()
const addClientFormStore = useAddClientFormStore()
const addPaymentFormStore = useAddPaymentFormStore()
const addServiceCategoryFormStore = useAddServiceCategoryFormStore()
const addServiceTypeFormStore = useAddServiceTypeFormStore()
const addSubscriptionTypeFormStore = useAddSubscriptionTypeFormStore()
const subscriptionTypeCardStore = useSubscriptionTypeCardStore()
const addBusinessServiceFormStore = useAddBusinessServiceFormStore()
const addBookingTypeFormStore = useAddBookingTypeFormStore()
const groupCardStore = useGroupCardStore()
const addGroupFormStore = useAddGroupFormStore()

const selectedClientId = computed<string | null>(() => clientCardStore.selectedClientId)
const selectedPaymentId = computed<string | null>(() => paymentCardStore.selectedPaymentId)
const selectedBusinessServiceId = computed<string | null>(
  () => businessServiceCardStore.selectedBusinessServiceId,
)
const selectedBusinessServiceTypeId = computed<string | null>(
  () => businessServiceTypeCardStore.selectedBusinessServiceTypeId,
)
const selectedBusinessServiceNameId = computed<string | null>(
  () => businessServiceNameCardStore.selectedBusinessServiceNameId,
)
const selectedBookingTypeId = computed<string | null>(
  () => bookingTypeCardStore.selectedBookingTypeId,
)
const selectedGroupId = computed<string | null>(() => groupCardStore.selectedGroupId)
const selectedSubscriptionTypeId = computed<string | null>(
  () => subscriptionTypeCardStore.selectedSubscriptionTypeId,
)
</script>

<template>
  <aside class="info-view-layout font-tufte text-tufte-text" aria-label="Info view">
    <AddClientForm v-if="addClientFormStore.isOpen" />
    <AddPaymentForm v-else-if="addPaymentFormStore.isOpen" />
    <AddServiceCategoryForm v-else-if="addServiceCategoryFormStore.isOpen" />
    <AddServiceTypeForm v-else-if="addServiceTypeFormStore.isOpen" />
    <AddSubscriptionTypeForm v-else-if="addSubscriptionTypeFormStore.isOpen" />
    <AddServiceForm
      v-else-if="addBusinessServiceFormStore.isOpen"
      :key="addBusinessServiceFormStore.editId ?? 'new'"
    />
    <AddBookingTypeForm v-else-if="addBookingTypeFormStore.isOpen" />
    <AddGroupForm v-else-if="addGroupFormStore.isOpen" />
    <PaymentCard v-else-if="selectedPaymentId !== null" :payment-id="selectedPaymentId" />
    <ClientCard
      v-else-if="selectedClientId !== null"
      :key="selectedClientId"
      :client-id="selectedClientId"
    />
    <BusinessServiceCard
      v-else-if="selectedBusinessServiceId !== null"
      :key="selectedBusinessServiceId"
      :business-service-id="selectedBusinessServiceId"
    />
    <BusinessServiceTypeCard
      v-else-if="selectedBusinessServiceTypeId !== null"
      :business-service-type-id="selectedBusinessServiceTypeId"
    />
    <ServiceCategoryCard
      v-else-if="selectedBusinessServiceNameId !== null"
      :key="selectedBusinessServiceNameId"
      :business-service-name-id="selectedBusinessServiceNameId"
    />
    <BookingTypeCard
      v-else-if="selectedBookingTypeId !== null"
      :key="selectedBookingTypeId"
      :booking-type-id="selectedBookingTypeId"
    />
    <GroupCard
      v-else-if="selectedGroupId !== null"
      :key="selectedGroupId"
      :group-id="selectedGroupId"
    />
    <SubscriptionTypeCard
      v-else-if="selectedSubscriptionTypeId !== null"
      :key="selectedSubscriptionTypeId"
      :subscription-type-id="selectedSubscriptionTypeId"
    />
    <!-- Intentionally empty when no row is selected. Replace this branch
         with future per-tab info components when those features land. -->
  </aside>
</template>

<style scoped>
.info-view-layout {
  /*
   * `flex: 1 1 0` with a matching value on `ConversationLayout` makes the two
   * flanks share all leftover horizontal space equally. A 1px right border
   * separates the panel from the routed middle column so the eye can tell at
   * a glance that the card and the table belong to different surfaces — this
   * matters most when a client is selected and both sides carry similar
   * dense data. The border uses the same `--color-tufte-border` as the card's
   * internal section dividers, so the seam reads as one more horizontal
   * rule rather than a heavy frame.
   */
  flex: 1 1 0;
  min-width: var(--min-width-info-view);
  height: 100%;
  border-right: 1px solid var(--color-tufte-border);
  /*
   * The hosted `ClientCard` manages its own internal scroll (the visits
   * table scrolls inside the card while the upper sections stay pinned).
   * Letting the column scroll as well caused two nested scrollbars on
   * narrow viewports, so the column itself only handles overflow when no
   * card is mounted.
   */
  overflow: hidden;
  background-color: var(--color-tufte-bg);
  /*
   * Center the hosted card vertically inside the panel so short cards
   * (clients with few visits / no contact info) sit visually balanced
   * rather than pinned to the top edge while the rest of the column
   * stays empty. When the card grows taller than the panel its
   * `max-height: 100%` makes it span the full panel height, the visits
   * table takes over scrolling internally, and `justify-content: center`
   * becomes a no-op — both endpoints look correct.
   */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}
</style>
